import * as React from 'react'
import { theme } from '../theme'
import {
    TextAlignLeftIcon,
    TextAlignCenterIcon,
    FontSizeIncreaseIcon,
    FontSizeDecreaseIcon
} from '../icons'

interface Props {
    onAlignLeft?: () => void
    onAlignCenter?: () => void
    onIncreaseFontSize?: () => void
    onDecreaseFontSize?: () => void
    currentAlignment?: 'left' | 'center'
    disabled?: boolean
}

// Base button style for control buttons
const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: theme.borderRadius.sm,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.surface,
    cursor: 'pointer',
    opacity: 1,
    transition: 'all 0.2s ease',
}

// Style for active alignment button
const activeButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
}

// Container styles
const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    padding: `${theme.spacing.sm} 0`,
}

// Group container style
const groupStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
}

/**
 * Component for text formatting controls (alignment and font size)
 * To be placed under the disclaimer text box
 */
export const TextFormattingControls = ({
    onAlignLeft,
    onAlignCenter,
    onIncreaseFontSize,
    onDecreaseFontSize,
    currentAlignment = 'left',
    disabled = false
}: Props): JSX.Element => {
    // Hover effect styles using React's useState and event handlers
    const [hoveredButton, setHoveredButton] = React.useState<string | null>(null)

    const getButtonStyle = (buttonId: string, isActive = false) => {
        const baseStyle = isActive ? activeButtonStyle : buttonStyle

        // Apply disabled styles
        if (disabled) {
            return {
                ...baseStyle,
                cursor: 'not-allowed',
                opacity: 0.5
            }
        }

        // Apply hover styles
        if (hoveredButton === buttonId) {
            return {
                ...baseStyle,
                backgroundColor: isActive ? theme.colors.primary : theme.colors.background,
                transform: 'scale(1.05)',
                boxShadow: theme.shadows.sm
            }
        }

        return baseStyle
    }

    return (
        <div style={containerStyle}>
            {/* Text alignment controls */}
            <div style={groupStyle}>
                <button
                    type="button"
                    onClick={onAlignLeft}
                    disabled={disabled}
                    style={getButtonStyle('align-left', currentAlignment === 'left')}
                    title="Align text left"
                    onMouseEnter={() => setHoveredButton('align-left')}
                    onMouseLeave={() => setHoveredButton(null)}
                >
                    <TextAlignLeftIcon
                        size={20}
                        color={currentAlignment === 'left' ? 'white' : theme.colors.text.secondary}
                    />
                </button>
                <button
                    type="button"
                    onClick={onAlignCenter}
                    disabled={disabled}
                    style={getButtonStyle('align-center', currentAlignment === 'center')}
                    title="Align text center"
                    onMouseEnter={() => setHoveredButton('align-center')}
                    onMouseLeave={() => setHoveredButton(null)}
                >
                    <TextAlignCenterIcon
                        size={20}
                        color={currentAlignment === 'center' ? 'white' : theme.colors.text.secondary}
                    />
                </button>
            </div>

            {/* Font size controls */}
            <div style={groupStyle}>
                <button
                    type="button"
                    onClick={onIncreaseFontSize}
                    disabled={disabled}
                    style={getButtonStyle('font-increase')}
                    title="Increase font size"
                    onMouseEnter={() => setHoveredButton('font-increase')}
                    onMouseLeave={() => setHoveredButton(null)}
                >
                    <FontSizeIncreaseIcon size={20} color={theme.colors.text.secondary} />
                </button>
                <button
                    type="button"
                    onClick={onDecreaseFontSize}
                    disabled={disabled}
                    style={getButtonStyle('font-decrease')}
                    title="Decrease font size"
                    onMouseEnter={() => setHoveredButton('font-decrease')}
                    onMouseLeave={() => setHoveredButton(null)}
                >
                    <FontSizeDecreaseIcon size={20} color={theme.colors.text.secondary} />
                </button>
            </div>
        </div>
    )
}
